import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider, useAuth } from "@/context/AuthContext"
import { ToastProvider } from "@/context/ToastContext"
import { AdminLayout }          from "@/components/layout/AdminLayout"
import { LoginPage }            from "@/pages/LoginPage"
import { DashboardPage }        from "@/pages/DashboardPage"
import { ProductsPage }         from "@/pages/ProductsPage"
import { CompanySlotsPage }     from "@/pages/CompanySlotsPage"
import { EarningsPage }         from "@/pages/EarningsPage"
import { UsersPage }            from "@/pages/UsersPage"
import { GracePeriodsPage }     from "@/pages/GracePeriodsPage"
import { SuspendedPage }        from "@/pages/SuspendedPage"
import { TransfersPage }        from "@/pages/TransfersPage"
import { CommissionsPage }      from "@/pages/CommissionsPage"
import { AdjustmentsPage }      from "@/pages/AdjustmentsPage"
import { PayoutsPage }          from "@/pages/PayoutsPage"
import { WalletsPage }          from "@/pages/WalletsPage"
import { BankAccountsPage }     from "@/pages/BankAccountsPage"
import { W9FormsPage }          from "@/pages/W9FormsPage"
import { NACHABatchesPage }     from "@/pages/NACHABatchesPage"
import { AnnouncementsPage }    from "@/pages/AnnouncementsPage"
import { AuditLogPage }         from "@/pages/AuditLogPage"
import { SettingsPage }         from "@/pages/SettingsPage"
import { CommissionRatesPage }  from "@/pages/CommissionRatesPage"

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading, isAdmin } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zimrah-950">
      <div className="flex flex-col items-center gap-3">
        <div className="w-7 h-7 rounded-full border-2 border-zimrah-800 border-t-zimrah-500 animate-spin" />
        <p className="text-zimrah-600 text-sm">Loadingâ€¦</p>
      </div>
    </div>
  )
  if (!session) return <Navigate to="/login" replace />
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zimrah-950">
        <div className="max-w-md rounded-2xl border border-red-900/50 bg-red-950/30 p-8 text-center">
          <h1 className="text-xl font-bold text-red-200 mb-2">Admin access required</h1>
          <p className="text-sm text-red-300">Your account is signed in, but it does not have administrator privileges.</p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index                element={<DashboardPage />} />
              <Route path="products"      element={<ProductsPage />} />
              <Route path="company-slots" element={<CompanySlotsPage />} />
              <Route path="earnings"      element={<EarningsPage />} />
              <Route path="users"         element={<UsersPage />} />
              <Route path="grace-periods" element={<GracePeriodsPage />} />
              <Route path="suspended"     element={<SuspendedPage />} />
              <Route path="transfers"     element={<TransfersPage />} />
              <Route path="commissions"   element={<CommissionsPage />} />
              <Route path="adjustments"   element={<AdjustmentsPage />} />
              <Route path="payouts"       element={<PayoutsPage />} />
              <Route path="wallets"       element={<WalletsPage />} />
              <Route path="bank-accounts" element={<BankAccountsPage />} />
              <Route path="w9"            element={<W9FormsPage />} />
              <Route path="batches"       element={<NACHABatchesPage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="audit"         element={<AuditLogPage />} />
              <Route path="settings"      element={<SettingsPage />} />
              <Route path="rates"         element={<CommissionRatesPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  )
}